import React, { useMemo, useState } from 'react'
import { Input, Stack, Typography } from '@prometheus/components'
import { bossMissileMaxDamage, bossMissileUCost } from '@prometheus/utils'
import { useSelector } from '../../../../hooks/useSelector'
import usePlayerActions from '../../hooks/usePlayerActions'
import PromiseButtonV2 from '@/components/PromiseButtonV2'
import { MissileSprite } from './BossSprites'

/**
 * The missile volley controls, shared by the BossPanel and the board-click
 * attack modal. All numbers come from `@prometheus/utils` `bossInvader.ts` —
 * the same functions the server charges and rolls with, so what the form
 * promises is what the volley does.
 */
export const BossAttackForm = ({ onDone }: { onDone?: () => void }) => {
  const sheet = useSelector(s => s.game.sheet)
  const { onAttackBoss } = usePlayerActions()
  const [missiles, setMissiles] = useState(1)
  const [firing, setFiring] = useState(false)

  const weaponsLevel = sheet?.weapons?.current_level || 1
  const costPerMissile = bossMissileUCost(weaponsLevel)
  const maxDamage = bossMissileMaxDamage(weaponsLevel)
  const uranium = sheet?.resources?.U ?? 0
  const attackSpent = (sheet?.attacks_used ?? 0) > 0
  const totalCost = missiles * costPerMissile

  const hint = useMemo(() => {
    if (attackSpent) {
      return 'You have already fired your missiles this year.'
    }
    if (uranium < costPerMissile) {
      return `Missiles cost ${costPerMissile} uranium each at weapons level ${weaponsLevel} — you have ${uranium}.`
    }
    if (totalCost > uranium) {
      return `${missiles} missiles cost ${totalCost} uranium — you have ${uranium}.`
    }
    return ''
  }, [attackSpent, uranium, costPerMissile, totalCost, missiles, weaponsLevel])

  const blocked = attackSpent || missiles < 1 || totalCost > uranium

  return (
    <Stack space="sm" className="pg-boss-attack">
      <div className="pg-boss-attack__row">
        <Input
          type="number"
          label="Missiles"
          value={missiles}
          min={1}
          step={1}
          onChange={(e: any) => setMissiles(Math.max(1, Math.floor(Number(e.target.value) || 1)))}
        />
        <div className="pg-boss-attack__math">
          <Typography variant="caption">
            {costPerMissile} U each · rolls 1–{maxDamage} damage · weapons level {weaponsLevel}
          </Typography>
          <Typography variant="caption">
            ~{Math.round(missiles * (maxDamage + 1) / 2)} expected damage, {missiles * maxDamage} max
          </Typography>
        </div>
      </div>
      {firing && (
        <div className="pg-boss-attack__salvo" aria-hidden>
          <MissileSprite className="pg-boss-attack__salvo-missile" width={20} />
          <MissileSprite className="pg-boss-attack__salvo-missile" width={20} />
          <MissileSprite className="pg-boss-attack__salvo-missile" width={20} />
        </div>
      )}
      <PromiseButtonV2
        variant="danger"
        disabled={blocked}
        onClick={async () => {
          setFiring(true)
          try {
            await onAttackBoss(missiles)
          } finally {
            setFiring(false)
          }
          onDone?.()
        }}
      >
        <span className="pg-boss-attack__fire-label">
          <MissileSprite width={20} />
          Fire {missiles} {missiles > 1 ? 'missiles' : 'missile'} ({totalCost} U)
        </span>
      </PromiseButtonV2>
      {hint && <Typography variant="caption" color="warning">{hint}</Typography>}
    </Stack>
  )
}
